// src/pages/tenant/admin/AdminMigrationsPage.tsx

import { Fragment, useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { observer } from '@react';
import { useTenant, useI18n } from '@react/hooks';
import {
  Button,
  Card,
  CardContent,
  Badge,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableEmpty,
  ErrorAlert,
} from '@react/components';
import { StatusBadge } from '@/components/StatusBadge';
import { Select } from '@/components/Select';

type MigrationStatus =
  | 'PENDING_APPROVAL'
  | 'APPROVED'
  | 'IN_PROGRESS'
  | 'VALIDATING'
  | 'COMPLETED'
  | 'FAILED'
  | 'ROLLED_BACK'
  | 'CANCELLED';

const MIGRATION_STATUSES: MigrationStatus[] = [
  'PENDING_APPROVAL',
  'APPROVED',
  'IN_PROGRESS',
  'VALIDATING',
  'COMPLETED',
  'FAILED',
  'ROLLED_BACK',
  'CANCELLED',
];

function mapMigrationStatusToVariant(
  status: string,
): 'success' | 'warning' | 'error' | 'info' | 'secondary' {
  switch (status) {
    case 'PENDING_APPROVAL':
    case 'TENANT_MIGRATION_STATUS_PENDING_APPROVAL':
      return 'warning';
    case 'APPROVED':
    case 'TENANT_MIGRATION_STATUS_APPROVED':
      return 'info';
    case 'IN_PROGRESS':
    case 'TENANT_MIGRATION_STATUS_IN_PROGRESS':
      return 'info';
    case 'VALIDATING':
    case 'TENANT_MIGRATION_STATUS_VALIDATING':
      return 'info';
    case 'COMPLETED':
    case 'TENANT_MIGRATION_STATUS_COMPLETED':
      return 'success';
    case 'FAILED':
    case 'TENANT_MIGRATION_STATUS_FAILED':
      return 'error';
    case 'ROLLED_BACK':
    case 'TENANT_MIGRATION_STATUS_ROLLED_BACK':
      return 'warning';
    case 'CANCELLED':
    case 'TENANT_MIGRATION_STATUS_CANCELLED':
      return 'secondary';
    default:
      return 'secondary';
  }
}

function migrationStatusLabel(status: string): string {
  const normalized = status
    .replace('TENANT_MIGRATION_STATUS_', '')
    .replace(/_/g, ' ');
  switch (normalized.replace(/\s/g, '_')) {
    case 'PENDING_APPROVAL':
      return 'Pending';
    case 'APPROVED':
      return 'Approved';
    case 'IN_PROGRESS':
      return 'Migrating';
    case 'VALIDATING':
      return 'Validating';
    case 'COMPLETED':
      return 'Completed';
    case 'FAILED':
      return 'Failed';
    case 'ROLLED_BACK':
      return 'Rolled Back';
    case 'CANCELLED':
      return 'Cancelled';
    default:
      return normalized;
  }
}

function normalizeMigrationStatus(status: string): string {
  return status.replace('TENANT_MIGRATION_STATUS_', '');
}

function formatDate(dateStr: string | undefined | null): string {
  if (!dateStr) return '--';
  try {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return String(dateStr);
  }
}

/**
 * List and manage cross-platform tenant migrations.
 * Route: /admin/tenants/migrations
 */
export const AdminMigrationsPage = observer(() => {
  const store = useTenant();
  const { t } = useI18n();
  const [searchParams, setSearchParams] = useSearchParams();

  const [statusFilter, setStatusFilter] = useState(
    searchParams.get('status') || 'all',
  );
  const [migrations, setMigrations] = useState<any[]>([]);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const loadMigrations = useCallback(async () => {
    try {
      const params: any = {
        pagination: {
          cursor: searchParams.get('cursor') || '',
          limit: 20,
          direction: 'forward' as const,
        },
      };
      if (statusFilter !== 'all') {
        params.status = `TENANT_MIGRATION_STATUS_${statusFilter}`;
      }
      const response = await store.ListTenantMigrations(params);
      const items = response?.migrations?.items;
      setMigrations(Array.isArray(items) ? items : []);
    } catch {
      // Error is handled by store
      setMigrations([]);
    }
  }, [searchParams, statusFilter]);

  useEffect(() => {
    loadMigrations();
  }, [loadMigrations]);

  const handleFilterChange = (value: string) => {
    setStatusFilter(value);
    const params: Record<string, string> = {};
    if (value !== 'all') params.status = value;
    setSearchParams(params);
  };

  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleApprove = async (migration: any) => {
    const tenantId = migration.tenant?.id || migration.tenant_id;
    const migrationId = migration.entity?.id || migration.id;
    if (!tenantId || !migrationId) return;
    setActionInProgress(migrationId);
    try {
      await store.ApproveTenantMigration(tenantId, migrationId, {});
      await loadMigrations();
    } catch {
      // Error handled by store
    } finally {
      setActionInProgress(null);
    }
  };

  const handleCancel = async (migration: any) => {
    if (
      !confirm(
        t('tenant.admin.cancelMigrationConfirm'),
      )
    )
      return;
    const tenantId = migration.tenant?.id || migration.tenant_id;
    const migrationId = migration.entity?.id || migration.id;
    if (!tenantId || !migrationId) return;
    setActionInProgress(migrationId);
    try {
      await store.CancelTenantMigration(tenantId, migrationId, {});
      await loadMigrations();
    } catch {
      // Error handled by store
    } finally {
      setActionInProgress(null);
    }
  };

  const totalMigrations = migrations.length;
  const inProgressCount = migrations.filter(
    (m: any) =>
      m.status === 'IN_PROGRESS' || m.status === 'TENANT_MIGRATION_STATUS_IN_PROGRESS',
  ).length;
  const pendingCount = migrations.filter(
    (m: any) =>
      m.status === 'PENDING_APPROVAL' ||
      m.status === 'TENANT_MIGRATION_STATUS_PENDING_APPROVAL',
  ).length;

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="text-sm">
        <Link to="/admin/tenants" className="text-muted-foreground hover:text-foreground">
          {t('tenant.admin.breadcrumbAdmin')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <Link to="/admin/tenants" className="text-muted-foreground hover:text-foreground">
          {t('tenant.admin.breadcrumbTenants')}
        </Link>
        <span className="mx-2 text-muted-foreground">/</span>
        <span className="text-foreground">
          {t('tenant.admin.breadcrumbMigrations')}
        </span>
      </nav>

      {/* The tab already names this page; the heading stays for screen readers. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="sr-only">{t('tenant.admin.migrationsTitle')}</h1>
        <div className="flex gap-4">
          <span className="text-sm text-muted-foreground">
            {t('tenant.admin.total')}:{' '}
            <span className="font-semibold text-foreground">{totalMigrations}</span>
          </span>
          <span className="text-sm text-muted-foreground">
            {t('tenant.admin.inProgress')}:{' '}
            <span className="font-semibold text-blue-600">{inProgressCount}</span>
          </span>
          <span className="text-sm text-muted-foreground">
            {t('tenant.admin.pendingApproval')}:{' '}
            <span className="font-semibold text-yellow-600">{pendingCount}</span>
          </span>
        </div>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">
                {t('tenant.admin.filterStatus')}
              </label>
              <Select value={statusFilter} onChange={(e) => handleFilterChange(e.target.value)} fullWidth={false}>
                <option value="all">
                  {t('tenant.admin.allStatuses')}
                </option>
                {MIGRATION_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {migrationStatusLabel(s)}
                  </option>
                ))}
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Migrations table */}
      <Card>
        <Table striped hoverable>
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              <TableHead>
                {t('tenant.admin.migColTenant')}
              </TableHead>
              <TableHead>
                {t('tenant.admin.migColSource')}
              </TableHead>
              <TableHead>
                {t('tenant.admin.migColTarget')}
              </TableHead>
              <TableHead>
                {t('tenant.admin.migColStatus')}
              </TableHead>
              <TableHead>
                {t('tenant.admin.migColProgress')}
              </TableHead>
              <TableHead>
                {t('tenant.admin.migColInitiatedBy')}
              </TableHead>
              <TableHead>
                {t('tenant.admin.migColCreated')}
              </TableHead>
              <TableHead className="text-right">
                {t('common.actions')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {store.isLoading ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center py-8">
                  {t('common.loading')}
                </TableCell>
              </TableRow>
            ) : migrations.length === 0 ? (
              <TableEmpty
                colSpan={9}
                message={t('tenant.admin.noMigrations')}
              />
            ) : (
              migrations.map((migration: any) => {
                const migrationId = migration.entity?.id || migration.id;
                const rawStatus = normalizeMigrationStatus(
                  String(migration.status ?? ''),
                );
                const isExpanded = expandedIds.has(migrationId);
                const isProcessing = actionInProgress === migrationId;
                const isPending =
                  rawStatus === 'PENDING_APPROVAL';
                const isInProgress = rawStatus === 'IN_PROGRESS';
                const isTerminal =
                  rawStatus === 'COMPLETED' ||
                  rawStatus === 'FAILED' ||
                  rawStatus === 'CANCELLED' ||
                  rawStatus === 'ROLLED_BACK';
                const progressPercent =
                  migration.progress_percent ?? migration.progress ?? 0;

                return (
                  <Fragment key={migrationId}>
                    <TableRow>
                      <TableCell>
                        <button
                          onClick={() => toggleExpand(migrationId)}
                          className="p-1 rounded hover:bg-muted text-muted-foreground"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          <svg
                            className={`h-4 w-4 transition-transform ${
                              isExpanded ? 'rotate-90' : ''
                            }`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/admin/tenants/${migration.tenant?.id || migration.tenant_id || ''}`}
                          className="text-sm font-medium text-foreground hover:text-primary"
                        >
                          {String(
                            migration.tenant?.id || migration.tenant_id || '--',
                          )}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-foreground">
                          {String(migration.source_platform?.id ?? migration.source_platform ?? '--')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-foreground">
                          {String(migration.target_platform?.id ?? migration.target_platform ?? '--')}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <StatusBadge
                            variant={mapMigrationStatusToVariant(migration.status)}
                            label={migrationStatusLabel(
                              String(migration.status ?? ''),
                            )}
                            size="sm"
                            className={
                              isInProgress ? 'animate-pulse' : ''
                            }
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        {isInProgress ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                              <div
                                className="h-full rounded-full bg-primary transition-all duration-300"
                                style={{
                                  width: `${Math.min(100, Math.max(0, progressPercent))}%`,
                                }}
                              />
                            </div>
                            <span className="text-xs text-muted-foreground tabular-nums">
                              {progressPercent}%
                            </span>
                          </div>
                        ) : rawStatus === 'COMPLETED' ? (
                          <span className="text-xs text-green-600">100%</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">--</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-foreground">
                          {String(
                            migration.initiated_by?.display_name ||
                              migration.initiated_by?.entity?.id ||
                              '--',
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {formatDate(migration.created_at)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {isPending && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600"
                                disabled={isProcessing}
                                onClick={() => handleApprove(migration)}
                              >
                                {isProcessing
                                  ? t('common.processing')
                                  : t('tenant.admin.approve')}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-destructive"
                                disabled={isProcessing}
                                onClick={() => handleCancel(migration)}
                              >
                                {t('tenant.admin.reject')}
                              </Button>
                            </>
                          )}
                          {isInProgress && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-destructive"
                              disabled={isProcessing}
                              onClick={() => handleCancel(migration)}
                            >
                              {isProcessing
                                ? t('common.processing')
                                : t('common.cancel')}
                            </Button>
                          )}
                          {isTerminal && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleExpand(migrationId)}
                            >
                              {t('tenant.admin.viewDetails')}
                            </Button>
                          )}
                          {!isPending && !isInProgress && !isTerminal && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleExpand(migrationId)}
                            >
                              {t('tenant.admin.viewDetails')}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>

                    {/* Expandable detail row */}
                    {isExpanded && (
                      <TableRow>
                        <TableCell colSpan={9} className="bg-muted/50 p-0">
                          <div className="px-6 py-4 space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                              <div>
                                <p className="text-xs font-medium text-muted-foreground">
                                  {t('tenant.admin.migReason')}
                                </p>
                                <p className="text-sm text-foreground mt-1">
                                  {String(
                                    migration.migration_reason ||
                                      migration.reason ||
                                      '--',
                                  )}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs font-medium text-muted-foreground">
                                  {t('tenant.admin.migCurrentPhase')}
                                </p>
                                <p className="text-sm text-foreground mt-1">
                                  {String(
                                    migration.current_phase ??
                                      migration.phase ??
                                      '--',
                                  )}
                                </p>
                              </div>
                            </div>

                            {/* Configuration flags */}
                            <div>
                              <p className="text-xs font-medium text-muted-foreground mb-2">
                                {t('tenant.admin.migConfiguration')}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {[
                                  {
                                    key: 'include_members',
                                    label: t('tenant.admin.migIncludeMembers'),
                                  },
                                  {
                                    key: 'include_content',
                                    label: t('tenant.admin.migIncludeData'),
                                  },
                                  {
                                    key: 'include_audit_logs',
                                    label: 'Include audit logs',
                                  },
                                  {
                                    key: 'include_subscriptions',
                                    label: 'Include subscriptions',
                                  },
                                  {
                                    key: 'dry_run',
                                    label: 'Dry run',
                                  },
                                  {
                                    key: 'keep_source_readonly',
                                    label: 'Keep source read-only',
                                  },
                                ].map(({ key, label }) => {
                                  const enabled = migration[key] ?? false;
                                  return (
                                    <Badge
                                      key={key}
                                      variant={
                                        enabled
                                          ? ('success' as any)
                                          : ('secondary' as any)
                                      }
                                      size="sm"
                                    >
                                      {label}: {enabled ? 'Yes' : 'No'}
                                    </Badge>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Progress details */}
                            {isInProgress && (
                              <div>
                                <p className="text-xs font-medium text-muted-foreground mb-1">
                                  {t('tenant.admin.migProgress')}
                                </p>
                                <div className="flex items-center gap-3">
                                  <div className="flex-1 h-3 rounded-full bg-muted overflow-hidden">
                                    <div
                                      className="h-full rounded-full bg-primary transition-all duration-300"
                                      style={{
                                        width: `${Math.min(100, Math.max(0, progressPercent))}%`,
                                      }}
                                    />
                                  </div>
                                  <span className="text-sm font-semibold text-foreground tabular-nums">
                                    {progressPercent}%
                                  </span>
                                </div>
                              </div>
                            )}

                            {/* Error message if failed */}
                            {(rawStatus === 'FAILED' ||
                              rawStatus === 'ROLLED_BACK') &&
                              (migration.error_message || migration.error) && (
                                <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20">
                                  <p className="text-xs font-medium text-destructive mb-1">
                                    {t('tenant.admin.migError')}
                                  </p>
                                  <p className="text-sm text-destructive">
                                    {String(
                                      migration.error_message ||
                                        migration.error ||
                                        '--',
                                    )}
                                  </p>
                                </div>
                              )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                );
              })
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Pagination */}
      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">
          {t('tenant.admin.showing')} {migrations.length}{' '}
          {t('tenant.admin.migrationUnit')}
          {migrations.length !== 1 ? 's' : ''}
        </span>
        <div className="flex gap-2">
          <Button
            variant="secondary"
            size="sm"
            disabled={!searchParams.get('cursor')}
            onClick={() => {
              const params = Object.fromEntries(searchParams.entries());
              delete params.cursor;
              setSearchParams(params);
            }}
          >
            {t('common.first')}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            disabled={migrations.length < 20}
            onClick={() => {
              const lastItem = migrations[migrations.length - 1];
              const cursor = lastItem?.entity?.id || lastItem?.id || '';
              if (cursor) {
                const params = Object.fromEntries(searchParams.entries());
                params.cursor = cursor;
                setSearchParams(params);
              }
            }}
          >
            {t('common.next')}
          </Button>
        </div>
      </div>
    </div>
  );
});

export default AdminMigrationsPage;
